import React, { useState } from "react";
import { CheckIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Persona, personaDisplayNames } from "tekkr-common/dist/model/user/persona";
import Spinner from "../ui/spinner";
import { useUpdateOrgUserMutation } from "../../data/mutations/update-org-user";
import { useCurrentOrgUser, useOrgController } from "../../auth/org-provider";
import { cn } from "../../lib/utils";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";

interface Props {
    onSaved: () => void;
}

export function PersonaSelectionDialog(props: Props) {
    const currentUser = useCurrentOrgUser();
    const orgController = useOrgController();
    const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);

    const mutation = useUpdateOrgUserMutation({
        onSuccess: async () => {
            await orgController.refetch();
            props.onSaved();
        },
    });

    function handleSave() {
        if (!selectedPersona) return;
        mutation.mutate({
            userId: currentUser.id,
            persona: selectedPersona,
        });
    }

    const techCompanyPersonas: Persona[] = ["projectDriver", "techLeader"];
    const consultancyPersonas: Persona[] = ["consultant", "consultancyLeader"];

    function PersonaOption({ persona }: { persona: Persona }) {
        return (
            <Label
                key={persona}
                htmlFor={persona}
                className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors",
                    selectedPersona === persona
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50",
                    mutation.isPending && "cursor-not-allowed opacity-50"
                )}
            >
                <RadioGroupItem value={persona} id={persona} />
                <span className="font-medium">{personaDisplayNames[persona]}</span>
            </Label>
        );
    }

    return (
        <Dialog open={true}>
            <DialogContent disableClose={true} className="sm:max-w-md [&>button]:hidden">
                <DialogHeader className="my-2 text-center">
                    <DialogTitle className="text-center text-xl font-bold">What describes your role best?</DialogTitle>
                    <DialogDescription className="text-center">You can change this later if you want</DialogDescription>
                </DialogHeader>

                <RadioGroup
                    value={selectedPersona ?? undefined}
                    onValueChange={(v) => setSelectedPersona(v as Persona)}
                    disabled={mutation.isPending}
                    className="space-y-6"
                >
                    <div>
                        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            In a Tech Company
                        </h4>
                        <div className="space-y-2">
                            {techCompanyPersonas.map((persona) => (
                                <PersonaOption key={persona} persona={persona} />
                            ))}
                        </div>
                    </div>

                    <Separator />

                    <div>
                        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            In a Consultancy
                        </h4>
                        <div className="space-y-2">
                            {consultancyPersonas.map((persona) => (
                                <PersonaOption key={persona} persona={persona} />
                            ))}
                        </div>
                    </div>
                </RadioGroup>

                {mutation.isError && (
                    <div className="rounded-md border border-destructive px-4 py-2 text-center text-sm text-destructive">
                        Something went wrong. Please try again.
                    </div>
                )}

                <DialogFooter className="mt-4 sm:justify-end">
                    <Button disabled={!selectedPersona || mutation.isPending} onClick={handleSave}>
                        {mutation.isPending ? (
                            <Spinner />
                        ) : (
                            <>
                                <CheckIcon className="mr-2 h-4 w-4" />
                                Save
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
